import React, { useState, useEffect, useRef } from 'react';
import { backdropClose } from '../backdrop';

// Editor for the per-quest voice-over replacements
// (quest_data.voiceover_replacements): regexes run on every text on its way to
// TTS, before the stress glossary — «Эс-Сувейр» → «Эс Суве́йр» makes the name
// read right in every case without touching the prose. Like the glossary they
// are a rule: nothing is written into the texts.
//
// Every rule shows how many places it rewrites and, unfolded, each of them in
// context — counted by the server on the rules as typed, before a save.

const MATCHES_DELAY_MS = 400;

const ReplacementsEditor = ({ questId, onClose, onSaved }) => {
  const [rows, setRows] = useState(null); // null = loading
  const [loadError, setLoadError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [matches, setMatches] = useState(null);       // {rules: [{count, matches}], unstable}
  const [matchError, setMatchError] = useState(null);
  const [open, setOpen] = useState({});               // row key -> matches unfolded
  const nextKey = useRef(0);

  const keyed = (list) => (list || []).map((r) => ({ ...r, _key: `r${nextKey.current++}` }));
  const payload = (list) => list.map(({ pattern, replacement, note }) => ({ pattern, replacement, note }));

  useEffect(() => {
    let dead = false;
    fetch(`/api/pipeline/quest/${questId}/voiceover-replacements`, { credentials: 'include' })
      .then((res) => res.json().then((data) => ({ res, data })))
      .then(({ res, data }) => {
        if (dead) return;
        if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
        setRows(keyed(data.replacements));
      })
      .catch((e) => { if (!dead) setLoadError(e.message); });
    return () => { dead = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questId]);

  // Recount on every edit, a moment after typing stops.
  useEffect(() => {
    if (!rows) return undefined;
    let dead = false;
    const t = setTimeout(() => {
      fetch(`/api/pipeline/quest/${questId}/voiceover-replacements/matches`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ replacements: payload(rows) }),
      })
        .then((res) => res.json().then((data) => ({ res, data })))
        .then(({ res, data }) => {
          if (dead) return;
          if (!res.ok) { setMatches(null); setMatchError(data.error || `HTTP ${res.status}`); return; }
          setMatches(data);
          setMatchError(null);
        })
        .catch((e) => { if (!dead) setMatchError(e.message); });
    }, MATCHES_DELAY_MS);
    return () => { dead = true; clearTimeout(t); };
  }, [rows, questId]);

  const update = (key, patch) => {
    setRows((rs) => rs.map((r) => (r._key === key ? { ...r, ...patch } : r)));
    setDirty(true);
  };
  const addRow = () => {
    setRows((rs) => [...rs, ...keyed([{ pattern: '', replacement: '', note: '' }])]);
    setDirty(true);
  };
  const removeRow = (key) => {
    setRows((rs) => rs.filter((r) => r._key !== key));
    setDirty(true);
  };

  const save = async () => {
    setBusy(true);
    setSaveError(null);
    try {
      const res = await fetch(`/api/pipeline/quest/${questId}/voiceover-replacements`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ replacements: payload(rows) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setRows(keyed(data.replacements));
      setDirty(false);
      if (onSaved) onSaved(data.replacements);
    } catch (e) {
      setSaveError(e.message);
    } finally {
      setBusy(false);
    }
  };

  // The server drops empty rows, so the n-th counted rule is the n-th row
  // with a pattern.
  const countedIndex = (() => {
    const m = new Map();
    let n = 0;
    (rows || []).forEach((r) => { if (String(r.pattern || '').trim()) m.set(r._key, n++); });
    return m;
  })();
  const total = matches ? matches.rules.reduce((s, r) => s + r.count, 0) : null;

  const cell = { padding: '6px 8px', borderBottom: '1px solid var(--border,#eee)', verticalAlign: 'top' };
  const input = { width: '100%', boxSizing: 'border-box', fontSize: 13, padding: '3px 6px', fontFamily: 'monospace' };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 10000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} {...backdropClose(onClose)}>
      <div
        style={{ background: 'var(--bg,#fff)', borderRadius: 10, padding: 20, width: 900, maxWidth: '94vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <h3 style={{ margin: 0 }}>🔁 Voice-over replacements</h3>
          <span style={{ fontSize: 12, color: '#5f6368' }}>
            {rows ? `${rows.length} rule(s)` : ''}{total != null ? ` · ${total} match(es) in the texts` : ''}
          </span>
          <button onClick={addRow} disabled={!rows} style={{ marginLeft: 'auto', padding: '4px 10px', borderRadius: 4 }}>+ Add rule</button>
        </div>
        <div style={{ fontSize: 11, color: '#5f6368', marginBottom: 8 }}>
          Each rule is a JavaScript regex (flags «gu», case-sensitive — write [Ээ] for either
          case) run on every text on its way to TTS, before the stress glossary; the texts
          themselves stay as they are. The replacement takes $1, $&lt;name&gt;, $&amp;. Rules run
          top to bottom, and a rule must leave its own output alone — saving refuses one that
          doesn't. Example: <code>Эс-Сувейр</code> → <code>Эс Суве́йр</code> covers every case.
        </div>

        {loadError && <div style={{ color: '#c5221f', fontSize: 12, marginBottom: 8 }}>✗ {loadError}</div>}
        {!rows && !loadError && <div style={{ fontSize: 12, color: '#5f6368' }}>⏳ Loading…</div>}
        {matchError && <div style={{ color: '#c5221f', fontSize: 12, marginBottom: 8 }}>✗ {matchError}</div>}
        {matches && matches.unstable.length > 0 && (
          <div style={{ color: '#9a6700', fontSize: 12, marginBottom: 8 }}>
            ⚠ A second pass would change the text again (point {matches.unstable[0].point}, {matches.unstable[0].field}
            {matches.unstable.length > 1 ? ` and ${matches.unstable.length - 1} more` : ''}) — saving will refuse these rules.
          </div>
        )}

        {rows && (
          <div style={{ flex: 1, minHeight: 0, overflow: 'auto', border: '1px solid var(--border,#eee)', borderRadius: 6 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ position: 'sticky', top: 0, background: 'var(--bg,#fff)', zIndex: 1 }}>
                  {['Pattern (regex)', 'Replace with', 'Matches', 'Note', ''].map((h) => (
                    <th key={h} style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid var(--border,#ddd)', fontSize: 11, color: '#5f6368' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const k = countedIndex.get(r._key);
                  const m = matches && k != null ? matches.rules[k] : null;
                  return (
                    <React.Fragment key={r._key}>
                      <tr>
                        <td style={{ ...cell, width: '30%' }}>
                          <input style={input} value={r.pattern} onChange={(e) => update(r._key, { pattern: e.target.value })} placeholder="Эс-Сувейр" />
                        </td>
                        <td style={{ ...cell, width: '30%' }}>
                          <input style={input} value={r.replacement} onChange={(e) => update(r._key, { replacement: e.target.value })} placeholder="Эс Суве́йр" />
                        </td>
                        <td style={{ ...cell, whiteSpace: 'nowrap' }}>
                          {m ? (
                            <button
                              type="button"
                              disabled={!m.count}
                              onClick={() => setOpen((o) => ({ ...o, [r._key]: !o[r._key] }))}
                              style={{ fontSize: 12, padding: '2px 8px', borderRadius: 4, cursor: m.count ? 'pointer' : 'default' }}
                            >{m.count ? `${open[r._key] ? '▾' : '▸'} ${m.count}` : '0'}</button>
                          ) : <span style={{ fontSize: 12, color: '#5f6368' }}>—</span>}
                        </td>
                        <td style={cell}>
                          <input style={{ ...input, fontFamily: 'inherit' }} value={r.note || ''} onChange={(e) => update(r._key, { note: e.target.value })} />
                        </td>
                        <td style={cell}>
                          <button type="button" title="Delete this rule" onClick={() => removeRow(r._key)} style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}>🗑️</button>
                        </td>
                      </tr>
                      {m && open[r._key] && m.count > 0 && (
                        <tr>
                          <td colSpan={5} style={{ ...cell, background: 'var(--bg-subtle,#fafafa)' }}>
                            {m.matches.map((x, i) => (
                              <div key={i} style={{ fontSize: 12, padding: '2px 0' }}>
                                <span style={{ color: '#5f6368' }}>#{x.point} {x.name} · {x.field === 'description' ? 'description' : 'after'}:</span>{' '}
                                …{x.before}
                                <s style={{ color: '#c5221f' }}>{x.match}</s>
                                <b style={{ color: '#188038' }}>{x.result}</b>
                                {x.after}…
                              </div>
                            ))}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {!rows.length && (
                  <tr><td colSpan={5} style={{ padding: 12, fontSize: 12, color: '#5f6368' }}>No rules yet — «+ Add rule».</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {saveError && <div style={{ color: '#c5221f', fontSize: 12, marginTop: 8 }}>✗ {saveError}</div>}
        <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end', alignItems: 'center' }}>
          {dirty && <span style={{ fontSize: 11, color: '#9a6700', marginRight: 'auto' }}>Unsaved changes</span>}
          <button onClick={onClose}>Close</button>
          <button
            onClick={save}
            disabled={!rows || busy || !dirty}
            style={{ background: '#8e44ad', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}
          >{busy ? 'Saving…' : 'Save replacements'}</button>
        </div>
      </div>
    </div>
  );
};

export default ReplacementsEditor;
